/**
 * RL-991 questions from the patient app about a released lab result, in the
 * doctor's inbox (`/patient-questions`). Shapes follow ommri-care-api
 * `conversations.examples.ts` (STAFF_QUESTION_EXAMPLE, STAFF_QUESTION_LIST_ITEM, Q).
 */
export const QUESTION_STATUSES = ['open', 'answered', 'closed'] as const;
export type QuestionStatus = (typeof QUESTION_STATUSES)[number];
export type QuestionStatusFilter = QuestionStatus | 'all';

export type QuestionRouteReason =
  'ordering_doctor' | 'branch_inbox' | 'claimed' | 'reassigned';

export type QuestionResultFlag =
  'normal' | 'low' | 'high' | 'abnormal' | 'critical';

export interface QuestionStaffRef {
  id: string;
  fullName: string;
}

export interface QuestionMessage {
  id: string;
  authorType: 'patient' | 'staff';
  staff: QuestionStaffRef | null;
  text: string;
  sentAt: string;
}

export interface QuestionSubject {
  resultId: string;
  testName: string;
  labOrderId: string;
  orderNumber: string | null;
  /** null in the oversight view (`textShown: false`). */
  result: {
    value: string | null;
    unit: string | null;
    referenceRange: string | null;
    flag: QuestionResultFlag | string | null;
    releasedAt: string | null;
  } | null;
}

export interface StaffQuestion {
  id: string;
  status: QuestionStatus;
  topic: 'lab_result';
  patient: QuestionStaffRef;
  branch: { id: string; name: string };
  /** null = the branch's doctors (branch inbox). */
  routedTo: QuestionStaffRef | null;
  routeReason: QuestionRouteReason;
  /** false = oversight view (manager): no messages and no result value. */
  textShown: boolean;
  subject: QuestionSubject | null;
  /** Set while open: when the patient's last message arrived. */
  waitingSince: string | null;
  /** Detail only; null in the oversight view. */
  messages?: QuestionMessage[] | null;
  /** List only. */
  messageCount?: number;
  answeredAt: string | null;
  closedAt: string | null;
  closedBy: QuestionStaffRef | null;
  lastMessageAt: string;
  createdAt: string;
  version: number;
}

export interface ReplyQuestionInput {
  text: string;
  close?: boolean;
  version: number;
}

export interface CloseQuestionInput {
  version: number;
}

export interface RouteQuestionInput {
  doctorId: string | null;
  version: number;
}

/** MESSAGE_MAX in the API's conversation-rules.ts. */
export const QUESTION_LIMITS = { messageMax: 1000 } as const;

export const QUESTION_PERMISSIONS = {
  read: 'doctor_inbox:read',
  manage: 'doctor_inbox:manage',
  oversee: 'doctor_inbox:oversee',
} as const;
